import {useMemo, useState} from "react";
import {ServiceEventEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseServiceEventDataTableFilterProps = Omit<UseDataTableFilterProps<ServiceEventEntity>, 'filterConfig'>

export function useServiceEventDataTableFilter(props: UseServiceEventDataTableFilterProps): UseDataTableFilterResult<ServiceEventEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [eventTypeQuery, setEventTypeQuery] = useState<string | undefined>(undefined);
  const [eventTypeDebouncedQuery] = useDebouncedValue(eventTypeQuery, 200);

  const [sourceQuery, setSourceQuery] = useState<string | undefined>(undefined);
  const [sourceDebouncedQuery] = useDebouncedValue(sourceQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<ServiceEventEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: ({name}, query) => !name || !name.toLowerCase().includes(query.toLowerCase())
      },
      "eventType": {
        setQuery: setEventTypeQuery,
        query: eventTypeQuery,
        debouncedQuery: eventTypeDebouncedQuery,
        filter: ({eventType}, query) => {
          if (eventType) {
            const display = getFormattedDatabaseCode(eventType, t)
            return !eventType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
      "source": {
        setQuery: setSourceQuery,
        query: sourceQuery,
        debouncedQuery: sourceDebouncedQuery,
        filter: ({source}, query) => !source || !source.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    eventTypeQuery,
    eventTypeDebouncedQuery,
    sourceQuery,
    sourceDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
